// src/pages/EditQuotation.jsx
import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetAllDiamondsQuery } from "../toolkitServices/diamondApi";
import { useGetDiamondSievesQuery } from "../toolkitServices/diamondSieveApi";
import { useGetDiamondSizesQuery } from "../toolkitServices/diamondSizeApi";
import { useGetCountersQuery } from "../toolkitServices/counterApi";
import { useGetLatestRatesQuery } from "../toolkitServices/rateApi";

import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setDiamondList } from "../slices/diamondSlice";
import { setDiamondSieveList } from "../slices/diamondSieveSlice";
import { setDiamondSizeList } from "../slices/diamondSizeSlice";
import { setCounterList } from "../slices/counterSlice";
import { setLastestRateList } from "../slices/latestRateSlice";

import { useGetQuotationByIdQuery } from "../toolkitServices/quotationApi";
import QuotationForm from "../components/quotation/QuotationForm";

/* ------------------- helpers ------------------- */
const listFrom = (resp) =>
  resp?.data ?? resp?.rows ?? resp?.items ?? resp ?? [];

const mkOpt = (doc, labelKey = "name") =>
  doc
    ? {
        label: doc[labelKey] || doc.name || "",
        value: doc._id || doc.id || "",
        info: doc,
      }
    : null;

const findById = (arr = [], id) =>
  arr?.find?.((x) => String(x._id || x.id || x.value) === String(id));

// label helpers
const findBranch = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, branchName: "" }, "branchName");
const findCounter = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, counterName: "" }, "counterName");
const findCat = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, categoryName: "" }, "categoryName");
const findProd = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, productName: "" }, "productName");
const findDesign = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, designName: "" }, "designName");
const findPurity = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, purityName: "" }, "purityName");
const findStone = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, stoneName: "" }, "stoneName");
const findDiamond = (list, id) =>
  mkOpt(findById(list, id) || { _id: id, diamondName: "" }, "diamondName");

// safe option
const ensureOpt = (opt, id, fallbackLabel = "") => {
  const label =
    opt?.label ||
    fallbackLabel ||
    (typeof id === "string" ? `#${id.slice(-6)}` : "Unknown");
  const value = opt?.value ?? id ?? "";
  const info = opt?.info ?? (id ? { _id: id, name: label } : {});
  return { label, value, info };
};

/* ----------------- API doc -> Form shape ----------------- */
function toFormShape(doc, master) {
  const { branches, stones, diamonds, counters } = master;

  const branch = doc.branchId ? findBranch(branches, doc.branchId) : null;

  const customer = {
    label: doc?.customer?.customerId || "",
    value: doc?.customer?._id || "",
    info: doc?.customer || {},
  };

  const stockEntries = (doc.stockEntries || []).map((r) => {
    const counterOpt = r?.counterId ? findCounter(counters, r.counterId) : null;

    const stonesArr = (r.stones || []).map((s) => ({
      stnPopupName: s?.stoneId,
      unit: s.unit || "",
      shape: s.shape || null,
      color: s.color || null,
      size: s.size || null,
      settingType: s.settingType || null,
      stnPopupWt: s.wt,
      stnPopupRateByWt: s.rateByWt,
      stnPopupPcs: s.pcs,
      stnPopupRateByPcs: s.rateByPcs,
      stnPopupAmt: s.amount,
      stnPopupDeduct: s.deduct || "",
      stnPopupDescription: s.description || "",
    }));

    const diamondsArr = (r.diamonds || []).map((d) => ({
      diaPopupName: d?.diamondId,
      unit: d.unit || "carat",
      shape: d.shape || null,
      color: d.color || null,
      clarity: d.clarity || null,
      sieve: d.sieve || null,
      size: d.size || null,
      diaPopupWt: d.ct,
      diaPopupRateByCt: d.rateByCt,
      diaPopupPcs: d.pcs,
      diaPopupRateByPcs: d.rateByPcs,
      diaPopupAmt: d.amount,
      diaPopupDeduct: d.deduct || "",
      diaPopupDescription: d.description || "",
    }));

    return {
      itemCodeDetail: r.itemCode
        ? {
            label: r.itemCode,
            value: r.labelledItemId,
            info: { _id: r.labelledItemId },
          }
        : null,

      category: r.categoryId
        ? { value: r.categoryId, label: r.categoryName }
        : null,
      product: r.productId
        ? { value: r.productId, label: r.productName }
        : null,
      design: r.designId ? { value: r.designId, label: r.designName } : null,
      purity: r.purityId ? { value: r.purityId, label: r.purityName } : null,

      counterRow: counterOpt
        ? ensureOpt(counterOpt, r.counterId, r.counterName)
        : null,

      qty: r.qty,
      totalWt: r.totalWt,
      packingWt: r.packingWt,
      grossWt: r.grossWt,
      stoneWt: r.stoneWt,
      diamondWt: r.diamondWt,
      netWt: r.netWt,
      stonePcs: r.stonePcs,
      diamondPcs: r.diamondPcs,
      ratePerGram: r.ratePerGram,
      makingPercentage: r.makingPercentage,
      makingPerGram: r.makingPerGram,
      fixedWastage: r.fixedWastage,
      fixedAmount: r.fixedAmount,
      stoneAmount: r.stoneAmount,
      diamondAmount: r.diamondAmount,
      hallmarkAmount: r.hallmarkAmount,
      itemAmount: r.itemAmount,
      itemGstAmount: r.itemGstAmount,
      totalItemAmount: r.totalItemAmount,
      mrp: r.mrp,
      huidCode: r.huidCode,
      description: r.description,

      stones: stonesArr,
      diamonds: diamondsArr,
    };
  });

  const looseStones = (doc.looseStones || []).map((s) => {
    const stoneOpt = s.stoneId
      ? ensureOpt(findStone(stones, s.stoneId), s.stoneId, s.stoneName)
      : null;
    const counterOpt = s.counterId
      ? ensureOpt(findCounter(counters, s.counterId), s.counterId)
      : null;

    return {
      counterRow: counterOpt,
      stnPopupName: s?.stoneId ?? stoneOpt?.value ?? null,
      unit: s.unit || "",
      shape: s.shape || null,
      color: s.color || null,
      size: s.size || null,
      settingType: s.settingType || null,
      stnPopupWt: s.wt,
      stnPopupPcs: s.pcs,
      itemAmount: s.itemAmount,
      itemGstAmount: s.itemGstAmount,
      totalItemAmount: s.totalItemAmount,
      stnPopupDescription: s.description || "",
    };
  });

  const looseDiamonds = (doc.looseDiamonds || []).map((d) => {
    const counterOpt = d.counterId
      ? ensureOpt(findCounter(counters, d.counterId), d.counterId)
      : null;

    return {
      counterRow: counterOpt,
      diaName: d?.diamondId ?? null,
      unit: d.unit || "carat",
      shape: d.shape || null,
      color: d.color || null,
      clarity: d.clarity || null,
      sieve: d.sieve || null,
      size: d.size || null,
      diaWt: d.ct,
      pcs: d.pcs,
      itemAmount: d.itemAmount,
      itemGstAmount: d.itemGstAmount,
      totalItemAmount: d.totalItemAmount,
      description: d.description || "",
    };
  });

  return {
    _id: doc._id,
    customer,
    branch,
    quotationNo: String(doc.qtnNo ?? ""),
    quotationDate: (doc.qtnDate || "").slice(0, 10),
    validTill: (doc.validTill || "").slice(0, 10),

    stockEntries,
    looseStones,
    looseDiamonds,

    totalDiscount: doc.totals?.discount || "",
    courierCharge: doc.totals?.courier || "",

    fineGold: doc.fineGold || "",
    fineSilver: doc.fineSilver || "",
    advanceAmount: doc.advanceAmount || "",
    balanceAmount: doc.balanceAmount || "",
  };
}

/* --------------------------------- Component --------------------------------- */
const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: quotationResp, isFetching } = useGetQuotationByIdQuery(id);

  // masters fetch
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  const { data: products } = useGetAllProductsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: designs } = useGetAllDesignsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: purities } = useGetAllPuritiesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  const { data: stones } = useGetAllStonesQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: diamonds } = useGetAllDiamondsQuery({
    page: 1,
    limit: 2000,
    search: "",
  });
  const { data: diamondSieves } = useGetDiamondSievesQuery({
    page: 1,
    limit: 100,
  });
  const { data: diamondSizes } = useGetDiamondSizesQuery({
    page: 1,
    limit: 100,
  });
  const { data: counters } = useGetCountersQuery({
    page: 1,
    limit: 2000,
  });
  const { data: latestRates } = useGetLatestRatesQuery();

  // put masters in store (use listFrom)
  useEffect(() => {
    const catList = listFrom(categories);
    const prodList = listFrom(products);
    const desList = listFrom(designs);
    const purList = listFrom(purities);
    const brList = listFrom(branches);
    const stList = listFrom(stones);
    const diaList = listFrom(diamonds);
    const sieveList = listFrom(diamondSieves);
    const sizeList = listFrom(diamondSizes);
    const counterList = listFrom(counters);
    const rateList = listFrom(latestRates);

    if (catList.length) dispatch(setCategoryList(catList));
    if (prodList.length) dispatch(setProductList(prodList));
    if (desList.length) dispatch(setDesignList(desList));
    if (purList.length) dispatch(setPurityList(purList));
    if (brList.length) dispatch(setBranchList(brList));
    if (stList.length) dispatch(setStoneList(stList));
    if (diaList.length) dispatch(setDiamondList(diaList));
    if (sieveList.length) dispatch(setDiamondSieveList(sieveList));
    if (sizeList.length) dispatch(setDiamondSizeList(sizeList));
    if (counterList.length) dispatch(setCounterList(counterList));
    if (rateList.length) dispatch(setLastestRateList(rateList));
  }, [
    categories,
    products,
    designs,
    purities,
    branches,
    stones,
    diamonds,
    diamondSieves,
    diamondSizes,
    counters,
    latestRates,
    dispatch,
  ]);

  // keyboard: Ctrl+Backspace -> back
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // masters from store
  const categoriesState = useSelector((s) => s.categories?.categories || []);
  const productsState = useSelector((s) => s.products?.products || []);
  const designsState = useSelector((s) => s.designs?.designs || []);
  const puritiesState = useSelector((s) => s.purities?.purities || []);
  const branchesState = useSelector((s) => s.branches?.branches || []);
  const stonesState = useSelector((s) => s.stones?.stones || []);
  const diamondsState = useSelector((s) => s.diamonds?.diamonds || []);
  const countersState = useSelector((s) => s.counters?.counters || []);

  const existingData = useMemo(() => {
    const doc = quotationResp?.data || quotationResp;
    if (!doc) return null;

    return toFormShape(doc, {
      categories: categoriesState,
      products: productsState,
      designs: designsState,
      purities: puritiesState,
      branches: branchesState,
      stones: stonesState,
      diamonds: diamondsState,
      counters: countersState,
    });
  }, [
    quotationResp,
    categoriesState,
    productsState,
    designsState,
    puritiesState,
    branchesState,
    stonesState,
    diamondsState,
    countersState,
  ]);

  if (isFetching) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-8xl mx-auto rounded-xl relative max-h-[90vh] overflow-y-auto"
    >
      {existingData && (
        <QuotationForm
          key={existingData._id}
          existingData={existingData}
          mode="edit"
          itemId={existingData._id}
          allowMultipleRows={true}
        />
      )}
    </motion.div>
  );
};

export default EditQuotation;
